import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiBell, FiMenu, FiX, FiLogOut, FiUser, FiSettings, FiMoon, FiSun, FiHeart, FiHelpCircle } from 'react-icons/fi';
import NotificationPanel from '../common/NotificationPanel';
import logo from '../../assets/logo.png';
import useHeader from './useHeader';
import {
  HeaderContainer, LeftSection, CenterSection, DonateLink,
  MobileDonateIcon, Logo, LogoMark, IconSlot, RightSection, NotificationButton, NotificationBadge, UserMenu,
  UserButton, UserAvatar, FallbackAvatar, UserInfo, DropdownMenu, MenuItem, MobileMenuButton
} from './Header.styles';

// An icon that fills dark with light inner lines on hover (styled by its parent button/link).
function HeaderIcon({ icon: Icon, size = 20 }) {
  return (
    <IconSlot $size={size}>
      <Icon size={size} className="hdr-icon-base" />
      <Icon size={size} className="hdr-icon-lines" aria-hidden="true" focusable="false" />
    </IconSlot>
  );
}

function Header({ onMenuToggle, sidebarOpen }) {
  const {
    user, theme, toggleTheme, showUserMenu, setShowUserMenu, showNotificationPanel,
    setShowNotificationPanel, notificationCount, handleLogout, handleProfileClick,
    handleSettingsClick, handleNotificationClick, handleNotificationClose,
    handleNotificationUpdate, handleLogoClick
  } = useHeader();

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <MobileMenuButton onClick={onMenuToggle} title={sidebarOpen ? 'Hide navigation' : 'Show navigation'}>
            {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </MobileMenuButton>

          <Logo onClick={handleLogoClick}>
            <LogoMark $src={logo} role="img" aria-label="Faith Insight Logo" />
            <h1>Faith Insight</h1>
          </Logo>
        </LeftSection>

        <CenterSection>
          <DonateLink
            to="/donation"
            title="Make a Donation"
          >
            <HeaderIcon icon={FiHeart} size={18} />
            Donate & Support
          </DonateLink>
        </CenterSection>

        <RightSection>
        <MobileDonateIcon to="/donation" title="Donate & Support" aria-label="Donate & Support">
          <HeaderIcon icon={FiHeart} size={20} />
        </MobileDonateIcon>

        {/* Report an Issue — students and instructors only; admins don't submit tickets. */}
        {(user?.role === 'student' || user?.role === 'instructor') && (
          <NotificationButton as={Link} to="/support/report" title="Report an Issue">
            <HeaderIcon icon={FiHelpCircle} size={20} />
          </NotificationButton>
        )}

        <NotificationButton
          onClick={handleNotificationClick}
          data-notification-button
        >
          <HeaderIcon icon={FiBell} size={20} />
          {notificationCount > 0 && (
            <NotificationBadge>{notificationCount}</NotificationBadge>
          )}
        </NotificationButton>

        <NotificationButton
          onClick={toggleTheme}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          style={theme === 'dark' ? { color: 'var(--hdr-accent)' } : undefined}
        >
          <HeaderIcon icon={theme === 'light' ? FiMoon : FiSun} size={20} />
        </NotificationButton>

        <UserMenu data-user-menu>
          <UserButton
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotificationPanel(false);
            }}
            data-user-button
          >
            {user?.avatar_url ? (
              <UserAvatar
                src={user.avatar_url}
                alt={`${user?.first_name} ${user?.last_name}`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <FallbackAvatar style={{ display: user?.avatar_url ? 'none' : 'flex' }}>
              {(user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')}
            </FallbackAvatar>
            <UserInfo>
              <div className="name">{user?.first_name} {user?.last_name}</div>
              <div className="role" style={{
                color: user?.role === 'admin' ? 'var(--hdr-role-admin)' : 'var(--hdr-muted)',
                fontWeight: user?.role === 'admin' ? '600' : '400'
              }}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </div>
            </UserInfo>
          </UserButton>

          <AnimatePresence>
            {showUserMenu && (
              <DropdownMenu
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <FiUser size={16} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleSettingsClick}>
                  <FiSettings size={16} />
                  Settings
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <FiLogOut size={16} />
                  Sign Out
                </MenuItem>
              </DropdownMenu>
            )}
          </AnimatePresence>
        </UserMenu>

        {/* Notification Panel */}
        <NotificationPanel
          isOpen={showNotificationPanel}
          onClose={handleNotificationClose}
          onUpdate={handleNotificationUpdate}
        />
        </RightSection>
      </HeaderContainer>
    </>
  );
}

export default Header;
